import { useEffect, useRef, useState } from 'preact/hooks'
import type { Bloc, Champ, Condition } from '@funo/grammaire'
import { changerSousCondition, comparerCondition, conditionInitiale, type ComparaisonSimple, type SourceCondition } from './conditions-editeur.ts'

export type BlocFormulaire = Extract<Bloc, { bloc: 'questions' }>
export interface ModeleFormulaire {
  id: string
  nom: string
  description: string
  bloc: BlocFormulaire
  origine: 'integre' | 'organisation'
}
export const TYPES_CHAMP: Array<{
  type: Champ['type']
  nom: string
  symbole: string
}> = [
  { type: 'texte', nom: 'Réponse courte', symbole: 'Aa' },
  { type: 'texte_long', nom: 'Paragraphe', symbole: '¶' },
  { type: 'choix', nom: 'Liste de choix', symbole: '☷' },
  { type: 'oui_non', nom: 'Oui ou non', symbole: '✓' },
  { type: 'nombre', nom: 'Nombre', symbole: '#' },
  { type: 'date', nom: 'Date', symbole: '▦' },
  { type: 'courriel', nom: 'Courriel', symbole: '@' },
  { type: 'telephone', nom: 'Téléphone', symbole: '+' },
  { type: 'fichier', nom: 'Pièces jointes', symbole: '↥' },
]
export const cleLocale = (prefixe: string) =>
  prefixe + '-' + crypto.randomUUID().slice(0, 8)
export function nouveauChamp(type: Champ['type'] = 'texte'): Champ {
  return {
    cle: cleLocale('question'),
    libelle:
      type === 'choix'
        ? 'Votre choix'
        : type === 'oui_non'
          ? 'Votre réponse'
          : 'Nouvelle question',
    type,
    obligatoire: false,
    sur_lhonneur: false,
    options:
      type === 'choix'
        ? [
            { valeur: 'option-1', libelle: 'Premier choix' },
            { valeur: 'option-2', libelle: 'Deuxième choix' },
          ]
        : [],
  }
}
export function deplacer<T>(items: T[], depuis: number, vers: number): T[] {
  if (depuis < 0 || vers < 0 || depuis >= items.length || vers >= items.length)
    return items
  const copie = [...items]
  const [item] = copie.splice(depuis, 1)
  copie.splice(vers, 0, item!)
  return copie
}
export async function apiBibliotheque<T>(
  base: string,
  chemin: string,
  method = 'GET',
  corps?: unknown,
): Promise<T> {
  const r = await fetch(base.replace(/\/$/, '') + '/api/admin/' + chemin, {
    method,
    headers: {
      Accept: 'application/json',
      ...(corps === undefined ? {} : { 'Content-Type': 'application/json' }),
    },
    ...(corps === undefined ? {} : { body: JSON.stringify(corps) }),
  })
  const valeur = await r.json().catch(() => ({}))
  if (!r.ok) {
    const erreur = new Error(
      (typeof valeur.erreur === 'string'
        ? valeur.erreur
        : valeur.erreur?.message) ??
        'Impossible de terminer cette action. Réessayez.',
    )
    Object.assign(erreur, { statut: r.status })
    throw erreur
  }
  return valeur as T
}

function RegleCondition({
  valeur,
  champs,
  changer,
  niveau = 0,
}: {
  valeur: Condition
  champs: SourceCondition[]
  changer(v?: Condition): void
  niveau?: number
}) {
  const groupe = 'tout' in valeur || 'un_de' in valeur
  const enfants = 'tout' in valeur ? valeur.tout : 'un_de' in valeur ? valeur.un_de : []
  const simple = 'champ' in valeur &&
    ('est' in valeur || 'pas' in valeur || 'min' in valeur || 'max' in valeur)
    ? valeur : null
  const source = simple ? champs.find((c) => c.fait === simple.champ) : undefined
  const operateur: ComparaisonSimple = simple && 'pas' in simple ? 'pas'
    : simple && 'min' in simple ? 'min' : simple && 'max' in simple ? 'max' : 'est'
  const contenu = simple ? Object.entries(simple).find(([k]) => k !== 'champ')?.[1] : ''
  const modifier = (op: ComparaisonSimple, v: unknown) => {
    if (!simple) return
    changer(comparerCondition(source ?? {
      fait: simple.champ,
      nom: simple.champ,
      type: typeof contenu === 'number' ? 'nombre' : typeof contenu === 'boolean' ? 'oui_non' : 'texte',
    }, op, v))
  }
  const ajouter = () => {
    if (!champs[0]) return
    const nouvelle = conditionInitiale(champs[0])
    changer('tout' in valeur ? { tout: [...valeur.tout, nouvelle] }
      : 'un_de' in valeur ? { un_de: [...valeur.un_de, nouvelle] }
      : { tout: [valeur, nouvelle] })
  }
  return (
    <div class="fb-condition-regle">
      {groupe ? (
        <>
          <label>
            Combiner les conditions
            <select
              aria-label="Combiner les conditions"
              value={'tout' in valeur ? 'tout' : 'un_de'}
              onChange={(e) => changer(e.currentTarget.value === 'tout' ? { tout: enfants } : { un_de: enfants })}
            >
              <option value="tout">Toutes sont remplies (ET)</option>
              <option value="un_de">Au moins une est remplie (OU)</option>
            </select>
          </label>
          <div class="fb-condition-enfants">
            {enfants.map((condition, i) => (
              <div key={i}>
                <div class="fb-condition-entete">
                  <strong>Condition {i + 1}</strong>
                  <button type="button" class="fb-lien fb-danger"
                    aria-label={`Retirer la condition ${i + 1}`}
                    onClick={() => changer(changerSousCondition(valeur as Extract<Condition, { tout: Condition[] } | { un_de: Condition[] }>, i))}>
                    Retirer
                  </button>
                </div>
                <RegleCondition valeur={condition} champs={champs} niveau={niveau + 1}
                  changer={(v) => changer(changerSousCondition(valeur as Extract<Condition, { tout: Condition[] } | { un_de: Condition[] }>, i, v))} />
              </div>
            ))}
          </div>
        </>
      ) : 'non' in valeur ? (
        <>
          <p class="fb-aide">La condition suivante doit être fausse.</p>
          <RegleCondition valeur={valeur.non} champs={champs} niveau={niveau + 1}
            changer={(v) => changer(v ? { non: v } : undefined)} />
        </>
      ) : simple ? (
        <>
          <label>
            Renseignement précédent
            <select aria-label="Renseignement précédent" value={simple.champ} onChange={(e) => {
              const c = champs.find((c) => c.fait === e.currentTarget.value)
              if (c) changer(conditionInitiale(c))
            }}>
              {!source ? <option value={simple.champ}>Condition existante · {simple.champ}</option> : null}
              {champs.map((c) => <option key={c.fait} value={c.fait}>{c.nom}</option>)}
            </select>
          </label>
          {!source ? <p class="fb-aide">Ce renseignement n’est pas disponible dans les étapes précédentes. Vérifiez l’ordre du parcours.</p> : null}
          <label>
            Comparaison
            <select aria-label="Comparaison" value={operateur} onChange={(e) => modifier(e.currentTarget.value as ComparaisonSimple, contenu)}>
              <option value="est">Est égale à</option>
              <option value="pas">Est différente de</option>
              {source?.type === 'nombre' || source?.type === 'date' || operateur === 'min' || operateur === 'max' ? <>
                <option value="min">Est au moins</option>
                <option value="max">Est au plus</option>
              </> : null}
            </select>
          </label>
          <label>
            Valeur
            {source?.type === 'oui_non' || typeof contenu === 'boolean' ? (
              <select aria-label="Valeur" value={String(contenu)} onChange={(e) => modifier(operateur, e.currentTarget.value)}>
                <option value="true">Oui</option><option value="false">Non</option>
              </select>
            ) : source?.options?.length ? (
              <select aria-label="Valeur" value={String(contenu)} onChange={(e) => modifier(operateur, e.currentTarget.value)}>
                <option value="">Choisir une réponse</option>
                {contenu !== '' && !source.options.some((o) => o.valeur === String(contenu)) ? (
                  <option value={String(contenu)}>Valeur existante · {String(contenu)}</option>
                ) : null}
                {source.options.map((o) => <option key={o.valeur} value={o.valeur}>{o.libelle}</option>)}
              </select>
            ) : (
              <input aria-label="Valeur" type={source?.type === 'nombre' || typeof contenu === 'number' ? 'number' : source?.type === 'date' ? 'date' : 'text'}
                step="any" value={String(contenu ?? '')} onInput={(e) => modifier(operateur, e.currentTarget.value)} />
            )}
          </label>
        </>
      ) : (
        <p class="fb-aide">Cette condition avancée reste active. Vous pouvez la combiner avec d’autres conditions ou la retirer.</p>
      )}
      {champs.length && (groupe || niveau < 3) ? (
        <button type="button" class="fb-lien" onClick={ajouter}>
          {groupe ? '+ Ajouter une condition' : '+ Combiner avec une autre condition'}
        </button>
      ) : null}
    </div>
  )
}

export function ConditionEditor({ valeur, champs, changer }: {
  valeur?: Condition
  champs: SourceCondition[]
  changer(v?: Condition): void
}) {
  return (
    <div class="fb-condition">
      <label class="fb-case">
        <input type="checkbox" checked={Boolean(valeur)} disabled={!valeur && !champs.length}
          onChange={(e) => changer(e.currentTarget.checked && champs[0] ? conditionInitiale(champs[0]) : undefined)} />
        <span>Afficher seulement si une condition est remplie</span>
      </label>
      {valeur ? <RegleCondition valeur={valeur} champs={champs} changer={changer} />
        : !champs.length ? <p class="fb-aide">Ajoutez une question ou une réservation précédente pour définir une condition.</p> : null}
    </div>
  )
}

export function ApercuFormulaire({
  champs,
  titre = 'Votre formulaire',
}: {
  champs: Champ[]
  titre?: string
}) {
  return (
    <div class="fb-apercu" aria-label="Aperçu du formulaire">
      <p class="fb-eyebrow">Ce que verra le public</p>
      <h3>{titre}</h3>
      <p class="fb-aide">
        Les questions obligatoires sont indiquées par un astérisque.
      </p>
      {champs.map((c) => (
        <div class="fb-preview-champ" key={c.cle}>
          <label>
            {c.libelle || 'Votre question'}
            {c.obligatoire ? <b> *</b> : null}
          </label>
          {c.type === 'fichier' ? (
            <div class="fb-faux-champ">Choisir des fichiers · PDF, JPEG, Excel ou Word</div>
          ) : c.type === 'texte_long' ? (
            <div class="fb-faux-champ fb-faux-texte">Votre réponse…</div>
          ) : c.type === 'oui_non' ? (
            <div class="fb-preview-choix">
              <span>○ Oui</span>
              <span>○ Non</span>
            </div>
          ) : c.type === 'choix' ? (
            <div class="fb-preview-choix">
              {c.options.map((o) => (
                <span key={o.valeur}>○ {o.libelle}</span>
              ))}
            </div>
          ) : (
            <div class="fb-faux-champ">
              {c.type === 'date'
                ? 'Choisir une date'
                : c.type === 'courriel'
                  ? 'vous@exemple.ca'
                  : c.type === 'nombre'
                    ? '0'
                    : 'Votre réponse…'}
            </div>
          )}
          {c.aide ? <small>{c.aide}</small> : null}
          {c.si ? (
            <small class="fb-condition-note">
              Visible selon une réponse précédente
            </small>
          ) : null}
        </div>
      ))}
    </div>
  )
}

export function EditeurChamps({
  champs,
  changer,
  precedents = [],
  titre = 'Votre formulaire',
  lecture = false,
  espace = 'reponses',
}: {
  champs: Champ[]
  changer(champs: Champ[]): void
  precedents?: Array<{
    fait: string
    nom: string
    type?: string
    options?: Champ['options']
  }>
  titre?: string
  lecture?: boolean
  espace?: 'reponses' | 'demandeur'
}) {
  const [selection, setSelection] = useState(champs[0]?.cle ?? ''),
    [apercu, setApercu] = useState(false),
    [annonce, setAnnonce] = useState('')
  const [suppression, setSuppression] = useState<string | null>(null),
    dialogue = useRef<HTMLDialogElement>(null)
  const glisse = useRef<number | null>(null)
  const proprietes = useRef<HTMLDivElement>(null)
  const allerAuxProprietes = () => {
    if (window.matchMedia('(max-width:720px)').matches)
      requestAnimationFrame(() =>
        proprietes.current?.scrollIntoView({
          block: 'start',
          behavior: 'smooth',
        }),
      )
  }
  const index = champs.findIndex((c) => c.cle === selection),
    actif = champs[index]
  useEffect(() => {
    if (!champs.some((c) => c.cle === selection))
      setSelection(champs[0]?.cle ?? '')
  }, [champs])
  useEffect(() => {
    if (suppression) dialogue.current?.showModal()
  }, [suppression])
  const miseAJour = (v: Partial<Champ>) =>
    changer(champs.map((c) => (c.cle === selection ? { ...c, ...v } : c)))
  const ajouter = (type: Champ['type']) => {
    const c = nouveauChamp(type)
    changer([...champs, c])
    setSelection(c.cle)
    setApercu(false)
    setAnnonce('Question ajoutée. Personnalisez son libellé.')
  }
  const bouger = (de: number, vers: number) => {
    changer(deplacer(champs, de, vers))
    setAnnonce('Question déplacée à la position ' + (vers + 1) + '.')
  }
  return (
    <div class="fb-editeur">
      <div class="fb-editeur-outils">
        <span>
          {champs.length} question{champs.length > 1 ? 's' : ''}
        </span>
        <button
          type="button"
          class={'fb-bouton ' + (apercu ? 'fb-actif' : '')}
          onClick={() => setApercu(!apercu)}
        >
          {apercu ? 'Revenir à l’édition' : 'Voir l’aperçu'}
        </button>
      </div>
      <div class="fb-editeur-grille">
        <div class="fb-champs-liste">
          <ol aria-label="Questions du formulaire">
            {champs.map((c, i) => (
              <li
                key={c.cle}
                class={selection === c.cle ? 'fb-champ-actif' : ''}
                draggable={!lecture}
                onDragStart={(e) => {
                  glisse.current = i
                  e.dataTransfer?.setData('text/plain', c.cle)
                }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault()
                  if (!lecture && glisse.current !== null)
                    bouger(glisse.current, i)
                  glisse.current = null
                }}
              >
                <button
                  type="button"
                  class="fb-selection"
                  onClick={() => {
                    setSelection(c.cle)
                    setApercu(false)
                    allerAuxProprietes()
                  }}
                >
                  <span class="fb-type-icone">
                    {TYPES_CHAMP.find((t) => t.type === c.type)?.symbole ??
                      'Aa'}
                  </span>
                  <span>
                    <strong>{c.libelle || 'Question sans titre'}</strong>
                    <small>
                      {TYPES_CHAMP.find((t) => t.type === c.type)?.nom}
                      {c.obligatoire ? ' · Obligatoire' : ''}
                      {c.si ? ' · Conditionnelle' : ''}
                    </small>
                  </span>
                </button>
                {!lecture ? (
                  <div class="fb-deplacer">
                    <button
                      type="button"
                      aria-label={'Monter ' + c.libelle}
                      disabled={i === 0}
                      onClick={() => bouger(i, i - 1)}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      aria-label={'Descendre ' + c.libelle}
                      disabled={i === champs.length - 1}
                      onClick={() => bouger(i, i + 1)}
                    >
                      ↓
                    </button>
                  </div>
                ) : null}
              </li>
            ))}
          </ol>
          {!lecture ? (
            <div class="fb-ajouter">
              <p>Ajouter une question</p>
              <div class="fb-types">
                {TYPES_CHAMP.map((t) => (
                  <button
                    type="button"
                    key={t.type}
                    onClick={() => ajouter(t.type)}
                  >
                    <span>{t.symbole}</span>
                    {t.nom}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </div>
        <div class="fb-proprietes" ref={proprietes}>
          {apercu ? (
            <ApercuFormulaire champs={champs} titre={titre} />
          ) : actif ? (
            <fieldset disabled={lecture}>
              <div class="fb-proprietes-titre">
                <span>Question {index + 1}</span>
                {!lecture ? (
                  <div>
                    <button
                      type="button"
                      class="fb-lien"
                      onClick={() => {
                        const c = {
                          ...structuredClone(actif),
                          cle: cleLocale('question'),
                          libelle: actif.libelle + ' (copie)',
                        }
                        changer([
                          ...champs.slice(0, index + 1),
                          c,
                          ...champs.slice(index + 1),
                        ])
                        setSelection(c.cle)
                      }}
                    >
                      Dupliquer
                    </button>
                    <button
                      type="button"
                      class="fb-lien fb-danger"
                      onClick={() => setSuppression(actif.cle)}
                    >
                      Retirer
                    </button>
                  </div>
                ) : null}
              </div>
              <label>
                Question posée
                <input
                  value={actif.libelle}
                  maxLength={250}
                  onInput={(e) => miseAJour({ libelle: e.currentTarget.value })}
                  placeholder="Ex. De quel équipement avez-vous besoin ?"
                />
              </label>
              <label>
                Type de réponse
                <select
                  value={actif.type}
                  onChange={(e) =>
                    miseAJour({
                      type: e.currentTarget.value as Champ['type'],
                      ...(e.currentTarget.value === 'choix' &&
                      !actif.options.length
                        ? {
                            options: [
                              { valeur: 'option-1', libelle: 'Premier choix' },
                            ],
                          }
                        : {}),
                    })
                  }
                >
                  {TYPES_CHAMP.map((t) => (
                    <option value={t.type} key={t.type}>
                      {t.nom}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Texte d’aide <small>Facultatif</small>
                <textarea
                  rows={2}
                  value={actif.aide ?? ''}
                  onInput={(e) => miseAJour({ aide: e.currentTarget.value })}
                  placeholder="Un exemple ou une précision utile."
                />
              </label>
              <label class="fb-case">
                <input
                  type="checkbox"
                  checked={actif.obligatoire}
                  onChange={(e) =>
                    miseAJour({ obligatoire: e.currentTarget.checked })
                  }
                />
                Réponse obligatoire
              </label>
              {actif.type === 'fichier' ? (
                <label>
                  Nombre maximal de fichiers
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={actif.nombre_fichiers_max ?? 5}
                    onInput={(e) => miseAJour({ nombre_fichiers_max: Number(e.currentTarget.value) })}
                  />
                  <small>8 Mo maximum par fichier. PDF, JPEG, XLS, XLSX, DOC ou DOCX.</small>
                </label>
              ) : null}
              {actif.type === 'choix' ? (
                <section class="fb-options">
                  <h4>Choix proposés</h4>
                  {actif.options.map((o, n) => (
                    <div key={o.valeur}>
                      <span>{n + 1}</span>
                      <input
                        aria-label={'Libellé du choix ' + (n + 1)}
                        value={o.libelle}
                        onInput={(e) =>
                          miseAJour({
                            options: actif.options.map((a, j) =>
                              j === n
                                ? { ...a, libelle: e.currentTarget.value }
                                : a,
                            ),
                          })
                        }
                      />
                      <button
                        type="button"
                        aria-label={'Retirer le choix ' + (n + 1)}
                        onClick={() =>
                          miseAJour({
                            options: actif.options.filter((_, j) => j !== n),
                          })
                        }
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    class="fb-lien"
                    onClick={() =>
                      miseAJour({
                        options: [
                          ...actif.options,
                          {
                            valeur: cleLocale('choix'),
                            libelle: 'Nouveau choix',
                          },
                        ],
                      })
                    }
                  >
                    + Ajouter un choix
                  </button>
                </section>
              ) : null}
              {actif.type === 'nombre' ? (
                <>
                  <div class="fb-deux">
                    <label>
                      Minimum
                      <input
                        type="number"
                        value={actif.minimum ?? ''}
                        onInput={(e) =>
                          miseAJour({
                            minimum:
                              e.currentTarget.value === ''
                                ? undefined
                                : Number(e.currentTarget.value),
                          })
                        }
                      />
                    </label>
                    <label>
                      Maximum
                      <input
                        type="number"
                        value={actif.maximum ?? ''}
                        onInput={(e) =>
                          miseAJour({
                            maximum:
                              e.currentTarget.value === ''
                                ? undefined
                                : Number(e.currentTarget.value),
                          })
                        }
                      />
                    </label>
                  </div>
                  <label class="fb-case">
                    <input
                      type="checkbox"
                      checked={actif.entier ?? false}
                      onChange={(e) =>
                        miseAJour({ entier: e.currentTarget.checked })
                      }
                    />
                    Uniquement des nombres entiers
                  </label>
                </>
              ) : null}
              <ConditionEditor
                valeur={actif.si}
                champs={[
                  ...precedents,
                  ...champs.slice(0, index).map((c) => ({
                    fait: espace + '.' + c.cle,
                    nom: c.libelle,
                    type: c.type,
                    options: c.options,
                  })),
                ]}
                changer={(si) => miseAJour({ si })}
              />
              <details class="fb-options-avancees">
                <summary>Réglages avancés</summary>
                <label class="fb-case">
                  <input
                    type="checkbox"
                    checked={actif.sur_lhonneur}
                    onChange={(e) =>
                      miseAJour({ sur_lhonneur: e.currentTarget.checked })
                    }
                  />
                  Déclaration sur l’honneur
                </label>
                <label>
                  Variable pour les documents
                  <input
                    readOnly
                    value={'{{' + espace + '.' + actif.cle + '}}'}
                    onFocus={(e) => e.currentTarget.select()}
                  />
                </label>
                <p class="fb-aide">
                  La référence de cette question est conservée pour les
                  conditions et les données déjà recueillies.
                </p>
              </details>
            </fieldset>
          ) : (
            <div class="fb-vide">
              <h3>Votre formulaire commence ici</h3>
              <p>
                Ajoutez une question à gauche, puis ajustez ses choix et son
                affichage.
              </p>
            </div>
          )}
        </div>
      </div>
      <p class="fb-annonce" role="status">
        {annonce}
      </p>
      {suppression ? (
        <dialog
          ref={dialogue}
          class="fb-dialogue"
          onCancel={() => setSuppression(null)}
          aria-label="Retirer une question"
        >
          <h2>Retirer cette question ?</h2>
          <p>
            Vérifiez les conditions et les documents qui utilisent sa réponse.
            Les anciennes réservations gardent leur version du formulaire.
          </p>
          <div class="fb-actions">
            <button
              type="button"
              class="fb-bouton"
              onClick={() => setSuppression(null)}
            >
              Conserver
            </button>
            <button
              type="button"
              class="fb-bouton fb-danger"
              onClick={() => {
                changer(champs.filter((c) => c.cle !== suppression))
                setSuppression(null)
              }}
            >
              Retirer la question
            </button>
          </div>
        </dialog>
      ) : null}
    </div>
  )
}

export function FormulairesPane({
  baseUrl = '',
  permissions,
  onAuth,
  visible = true,
}: {
  baseUrl?: string
  permissions: string[]
  onAuth?(): void
  visible?: boolean
}) {
  const [modeles, setModeles] = useState<ModeleFormulaire[]>([]),
    [charge, setCharge] = useState(true),
    [erreur, setErreur] = useState(''),
    [notice, setNotice] = useState('')
  const [edition, setEdition] = useState<{
      id?: string
      nom: string
      description: string
      bloc: BlocFormulaire
    } | null>(null),
    [modifie, setModifie] = useState(false),
    [occupe, setOccupe] = useState(false),
    [filtre, setFiltre] = useState(''),
    [vueListe, setVueListe] = useState(false)
  const peut = permissions.includes('services.modifier')
  const faute = (e: unknown) => {
    if ((e as { statut?: number }).statut === 401) onAuth?.()
    setErreur((e as Error).message)
  }
  const charger = async () => {
    try {
      const r = await apiBibliotheque<{ modeles: ModeleFormulaire[] }>(
        baseUrl,
        'formulaires',
      )
      setModeles(r.modeles)
    } catch (e) {
      faute(e)
    } finally {
      setCharge(false)
    }
  }
  useEffect(() => {
    if (visible) void charger()
  }, [baseUrl, visible])
  useEffect(() => {
    const prevenir = (e: BeforeUnloadEvent) => {
      if (modifie) e.preventDefault()
    }
    window.addEventListener('beforeunload', prevenir)
    return () => window.removeEventListener('beforeunload', prevenir)
  }, [modifie])
  function ouvrir(m?: ModeleFormulaire) {
    if (
      modifie &&
      !window.confirm(
        'Remplacer votre formulaire en cours sans enregistrer ses modifications ?',
      )
    )
      return
    setVueListe(false)
    setEdition(
      m
        ? {
            ...(m.origine === 'organisation' ? { id: m.id } : {}),
            nom: m.origine === 'integre' ? m.nom + ' · personnalisé' : m.nom,
            description: m.description,
            bloc: structuredClone(m.bloc),
          }
        : {
            nom: '',
            description: '',
            bloc: {
              bloc: 'questions',
              cle: cleLocale('formulaire'),
              titre: 'Votre formulaire',
              champs: [nouveauChamp()],
            },
          },
    )
    setModifie(false)
    setErreur('')
    setNotice('')
  }
  async function enregistrer() {
    if (!edition || occupe) return
    setOccupe(true)
    setErreur('')
    try {
      if (!edition.nom.trim()) throw new Error('Donnez un nom à votre modèle.')
      await apiBibliotheque(
        baseUrl,
        'formulaires' +
          (edition.id ? '/' + encodeURIComponent(edition.id) : ''),
        edition.id ? 'PUT' : 'POST',
        {
          nom: edition.nom,
          description: edition.description,
          bloc: edition.bloc,
        },
      )
      setModifie(false)
      setEdition(null)
      setNotice(
        'Le modèle est enregistré. Il est disponible dans vos parcours.',
      )
      await charger()
    } catch (e) {
      faute(e)
    } finally {
      setOccupe(false)
    }
  }
  async function retirerModele(m: ModeleFormulaire) {
    if (
      !window.confirm(
        'Retirer « ' +
          m.nom +
          ' » de la bibliothèque ? Les parcours qui en utilisent une copie restent disponibles.',
      )
    )
      return
    setOccupe(true)
    setErreur('')
    try {
      await apiBibliotheque(
        baseUrl,
        'formulaires/' + encodeURIComponent(m.id),
        'DELETE',
      )
      if (edition?.id === m.id) {
        setEdition(null)
        setModifie(false)
      }
      await charger()
      setNotice('Le modèle a été retiré de la bibliothèque.')
    } catch (e) {
      faute(e)
    } finally {
      setOccupe(false)
    }
  }
  const ajuster = (v: Partial<NonNullable<typeof edition>>) => {
    setEdition((e) => (e ? { ...e, ...v } : e))
    setModifie(true)
  }
  return (
    <section class="fb-root">
      <style>{stylesFormulaires}</style>
      {erreur ? (
        <p class="fb-erreur" role="alert">
          {erreur}
        </p>
      ) : null}
      {notice ? (
        <p class="fb-succes" role="status">
          {notice}
        </p>
      ) : null}
      {edition && !vueListe ? (
        <>
          <div class="fb-entete">
            <div>
              <button
                type="button"
                class="fb-lien"
                onClick={() => {
                  setVueListe(true)
                }}
              >
                ← Bibliothèque des formulaires
              </button>
              <h2>
                {edition.id
                  ? 'Personnaliser le formulaire'
                  : 'Créer votre modèle'}
              </h2>
              <p>
                Composez les questions, les choix et les conditions. Réutilisez
                ce modèle dans autant de parcours que vous le souhaitez.
              </p>
            </div>
            {peut ? (
              <button
                type="button"
                class="fb-bouton fb-primaire"
                disabled={occupe}
                onClick={() => void enregistrer()}
              >
                {occupe ? 'Enregistrement…' : 'Enregistrer le modèle'}
              </button>
            ) : null}
          </div>
          <fieldset class="fb-modele-meta" disabled={!peut || occupe}>
            <label>
              Nom du modèle
              <input
                value={edition.nom}
                onInput={(e) => ajuster({ nom: e.currentTarget.value })}
                placeholder="Ex. Besoins techniques"
              />
            </label>
            <label>
              Description
              <input
                value={edition.description}
                onInput={(e) => ajuster({ description: e.currentTarget.value })}
                placeholder="Pour quels événements est-il utile ?"
              />
            </label>
            <label>
              Titre présenté au public
              <input
                value={edition.bloc.titre ?? ''}
                onInput={(e) =>
                  ajuster({
                    bloc: { ...edition.bloc, titre: e.currentTarget.value },
                  })
                }
              />
            </label>
          </fieldset>
          <EditeurChamps
            champs={edition.bloc.champs}
            changer={(champs) => ajuster({ bloc: { ...edition.bloc, champs } })}
            titre={edition.bloc.titre}
            lecture={!peut || occupe}
          />
        </>
      ) : (
        <>
          <div class="fb-entete">
            <div>
              <h2>Des formulaires à votre façon</h2>
              <p>
                Partez d’un modèle, adaptez chaque question et ajoutez-le à
                votre parcours.
              </p>
            </div>
            {peut ? (
              <button
                type="button"
                class="fb-bouton fb-primaire"
                onClick={() => ouvrir()}
              >
                + Créer un formulaire
              </button>
            ) : null}
          </div>
          {edition && modifie ? (
            <div class="ev-brouillon-local">
              <span>Votre formulaire en cours est conservé.</span>
              <button type="button" onClick={() => setVueListe(false)}>
                Reprendre le formulaire
              </button>
            </div>
          ) : null}
          <label class="fb-recherche">
            Rechercher un modèle
            <input
              type="search"
              value={filtre}
              onInput={(e) => setFiltre(e.currentTarget.value)}
              placeholder="Activité, équipements, besoins…"
            />
          </label>
          {charge ? (
            <p role="status" class="fb-vide">
              Chargement des modèles…
            </p>
          ) : (
            <div class="fb-modeles">
              {modeles
                .filter((m) =>
                  (m.nom + ' ' + m.description)
                    .toLocaleLowerCase()
                    .includes(filtre.toLocaleLowerCase()),
                )
                .map((m) => (
                  <article key={m.id}>
                    <span class="fb-type-icone">☷</span>
                    <p class="fb-eyebrow">
                      {m.origine === 'integre'
                        ? 'Point de départ'
                        : 'Votre organisation'}
                    </p>
                    <h3>{m.nom}</h3>
                    <p>{m.description}</p>
                    <small>
                      {m.bloc.champs.length} questions ·{' '}
                      {m.bloc.champs.filter((c) => c.obligatoire).length}{' '}
                      obligatoires
                    </small>
                    <button
                      type="button"
                      class="fb-bouton"
                      disabled={occupe}
                      onClick={() => ouvrir(m)}
                    >
                      {peut
                        ? m.origine === 'integre'
                          ? 'Personnaliser une copie'
                          : 'Modifier le modèle'
                        : 'Consulter'}
                    </button>
                    {peut && m.origine === 'organisation' ? (
                      <button
                        type="button"
                        class="fb-lien"
                        disabled={occupe}
                        onClick={() => void retirerModele(m)}
                      >
                        Retirer le modèle
                      </button>
                    ) : null}
                  </article>
                ))}
            </div>
          )}
        </>
      )}
    </section>
  )
}

export const stylesFormulaires = `
.fb-root,.fb-editeur{--fb-bleu:#391d44;--fb-texte:#2a1632;--fb-gris:#75627d;--fb-ligne:#ece1f0;color:var(--fb-texte);font-size:14px;line-height:1.5}.fb-root *,.fb-editeur *{box-sizing:border-box}.fb-root h2,.fb-root h3,.fb-root h4,.fb-editeur h3,.fb-editeur h4{margin:0;letter-spacing:-.35px}.fb-root p{margin:8px 0}.fb-root fieldset,.fb-editeur fieldset{border:0;padding:0;margin:0;min-width:0}.fb-root label:not(.fb-case),.fb-editeur label:not(.fb-case){display:flex;flex-direction:column;gap:7px;font-size:13px;font-weight:500;color:#4e4153;margin-bottom:18px}.fb-root input:not([type=checkbox]),.fb-root select,.fb-root textarea,.fb-editeur input:not([type=checkbox]),.fb-editeur select,.fb-editeur textarea{width:100%;min-width:0;border:1px solid #dcd4e0;border-radius:10px;padding:11px 12px;min-height:44px;background:#fff;color:#2a1632;font:inherit}.fb-root input[type=checkbox],.fb-editeur input[type=checkbox]{width:18px!important;height:18px!important;min-height:18px!important;accent-color:#391d44;flex-shrink:0}.fb-case{display:flex;gap:9px;align-items:flex-start;cursor:pointer;font-size:13px;margin:16px 0}.fb-case input{margin-top:2px}.fb-bouton{border:1px solid #e1dae4;background:white;color:#2a1632;border-radius:10px;padding:10px 15px;min-height:42px;display:inline-flex;align-items:center;justify-content:center;gap:8px;font-size:13px;cursor:pointer}.fb-primaire{color:white;background:#391d44;border-color:#391d44}.fb-bouton:disabled{opacity:.45;cursor:default}.fb-lien{background:none;border:0;color:#391d44;padding:6px 0;min-height:32px;font-size:13px;cursor:pointer}.fb-danger{color:#b32d27}.fb-entete{display:flex;justify-content:space-between;gap:24px;align-items:center;margin:5px 0 28px}.fb-entete h2{font-size:24px}.fb-entete p{max-width:670px;color:#75627d;font-size:14px}.fb-entete>.fb-bouton{flex-shrink:0}.fb-recherche{max-width:390px}.fb-modeles{display:grid;grid-template-columns:repeat(auto-fill,minmax(250px,1fr));gap:18px}.fb-modeles article{background:white;border:1px solid #ece1f0;border-radius:16px;padding:24px;display:flex;flex-direction:column;align-items:flex-start;gap:10px}.fb-modeles h3{font-size:18px}.fb-modeles p:not(.fb-eyebrow){color:#75627d;flex:1;font-size:13px}.fb-modeles small{color:#75627d;font-size:12px}.fb-modeles .fb-bouton{margin-top:8px;width:100%}.fb-type-icone{width:36px;height:36px;display:inline-grid;place-items:center;background:#f7f1fa;color:#391d44;border-radius:9px;flex-shrink:0;font-size:16px}.fb-eyebrow{font-size:10px!important;letter-spacing:1px;color:#806b89;text-transform:uppercase}.fb-modele-meta{display:grid;grid-template-columns:1fr 1.4fr 1fr;gap:18px;background:white;border:1px solid #ece1f0!important;padding:22px!important;border-radius:14px;margin-bottom:22px!important}.fb-modele-meta label{margin:0!important}.fb-editeur{border:1px solid #ece1f0;background:white;border-radius:14px;overflow:hidden}.fb-editeur-outils{display:flex;align-items:center;justify-content:space-between;padding:16px 20px;border-bottom:1px solid #ece1f0;font-size:13px;color:#75627d}.fb-editeur-grille{display:grid;grid-template-columns:minmax(210px,.85fr) minmax(270px,1.3fr)}.fb-champs-liste{padding:15px;background:#fbf9fc;border-right:1px solid #ece1f0;min-width:0}.fb-champs-liste ol{list-style:none;margin:0;padding:0}.fb-champs-liste li{display:flex;align-items:center;gap:5px;border:1px solid #ece1f0;background:#fff;border-radius:10px;margin-bottom:9px;position:relative}.fb-champs-liste li.fb-champ-actif{border-color:#adaee7;box-shadow:0 0 0 2px #391d4413}.fb-selection{display:flex!important;align-items:center;justify-content:flex-start!important;gap:10px;background:none!important;color:#2a1632!important;border:0!important;text-align:left!important;padding:12px!important;width:100%;min-width:0}.fb-selection>span:last-child{min-width:0;flex:1}.fb-selection strong{display:block;font-size:12px;line-height:1.45;overflow-wrap:anywhere}.fb-selection small{display:block;color:#806b89;font-size:10px;margin-top:3px}.fb-deplacer{display:flex;flex-direction:column;padding-right:5px}.fb-deplacer button{width:27px;min-height:26px;padding:2px;background:none;color:#6b5a70;font-size:14px;border:0}.fb-deplacer button:disabled{opacity:.2}.fb-ajouter{margin-top:24px}.fb-ajouter>p{font-size:11px;color:#6b5a70;margin:0 0 10px}.fb-types{display:grid;grid-template-columns:1fr 1fr;gap:7px}.fb-types button{font-size:11px;text-align:left;display:flex;gap:7px;align-items:center;border:1px solid #ece1f0;background:#fff;border-radius:8px;min-height:40px;padding:9px 7px;color:#5d4e63}.fb-types button:hover{border-color:#b3b4e9;background:#faf6fc}.fb-types button>span{font-weight:600;color:#391d44}.fb-proprietes{padding:23px;min-width:0}.fb-proprietes-titre{display:flex;align-items:center;justify-content:space-between;gap:12px;color:#6b5a70;font-size:12px;margin-bottom:20px}.fb-proprietes-titre>div{display:flex;gap:12px}.fb-proprietes h4{font-size:13px;margin:20px 0 12px}.fb-options>div{display:flex;align-items:center;gap:8px;margin-bottom:8px}.fb-options>div>span{font-size:12px;color:#6b5a70}.fb-options>div>button{background:none;border:0;padding:5px;min-height:35px;font-size:20px;color:#806b89}.fb-deux{display:grid;grid-template-columns:1fr 1fr;gap:14px}.fb-condition{margin-top:22px;padding-top:5px;border-top:1px solid #eee4f1}.fb-condition-regle{padding:14px;border-radius:10px;background:#faf7fc;margin-top:12px}.fb-condition-enfants{border-left:2px solid #dce3ed;padding-left:10px}.fb-condition-enfants>div{margin-top:14px}.fb-condition-entete{display:flex;justify-content:space-between;gap:8px;align-items:center;font-size:11px}.fb-condition-regle .fb-condition-regle{padding:10px;background:#ffffffb0;margin-top:4px}.fb-condition-regle code{overflow-wrap:anywhere}.fb-condition-regle label:last-child{margin-bottom:0!important}.fb-options-avancees{margin-top:20px;border-top:1px solid #eee4f1;padding-top:15px;font-size:12px;color:#6b5a70}.fb-options-avancees summary{cursor:pointer}.fb-aide{font-size:12px!important;color:#6b5a70;line-height:1.6}.fb-vide{padding:40px 22px;text-align:center;color:#6b5a70}.fb-vide h3{font-size:18px;color:#39303d}.fb-faux-champ{border:1px solid #e3dce6;min-height:44px;padding:12px;border-radius:10px;color:#6b5a70;background:#fbf9fc;font-size:12px}.fb-faux-texte{height:95px}.fb-preview-champ{margin-top:24px}.fb-preview-champ>label{display:block!important;font-size:13px;margin-bottom:7px!important}.fb-preview-champ small{display:block;font-size:11px;color:#6b5a70;margin-top:6px}.fb-preview-choix{display:flex;gap:9px;flex-wrap:wrap}.fb-preview-choix span{padding:11px 14px;border:1px solid #e3dce6;border-radius:9px;font-size:12px}.fb-apercu h3{font-size:21px}.fb-condition-note{font-style:italic}.fb-annonce{font-size:12px;color:#391d44;padding:0 20px;margin:0}.fb-annonce:not(:empty){padding:10px 20px;border-top:1px solid #ece1f0}.fb-erreur,.fb-succes{padding:14px 18px;border-radius:10px;margin-bottom:18px!important;background:#fff0ed;color:#a32f25;font-size:13px}.fb-succes{background:#edf7f2;color:#246345}.fb-actions{display:flex;justify-content:flex-end;gap:10px;margin-top:25px}.fb-dialogue{border:1px solid #e2dbe5;border-radius:17px;padding:28px;max-width:480px;width:calc(100% - 32px);color:#2a1632}.fb-dialogue h2{font-size:22px}.fb-dialogue p{font-size:14px;color:#6b5a70;line-height:1.6}.fb-dialogue::backdrop{background:#2a163255;backdrop-filter:blur(3px)}.fb-root :focus-visible,.fb-editeur :focus-visible{outline:3px solid #babbeb;outline-offset:3px}@media(max-width:1100px){.fb-modele-meta{grid-template-columns:1fr 1fr}.fb-modele-meta label:last-child{grid-column:1/-1}}@media(max-width:700px){.fb-entete{flex-direction:column;align-items:stretch}.fb-modele-meta{grid-template-columns:1fr}.fb-modele-meta label:last-child{grid-column:auto}.fb-editeur-grille{grid-template-columns:1fr}.fb-champs-liste{border-right:0;border-bottom:1px solid #ece1f0}.fb-proprietes{padding:20px}.fb-types{grid-template-columns:repeat(2,1fr)}.fb-deplacer{flex-direction:row}.fb-deplacer button{width:34px;min-height:40px}.fb-selection strong{font-size:13px}.fb-root input:not([type=checkbox]),.fb-root select,.fb-root textarea,.fb-editeur input:not([type=checkbox]),.fb-editeur select,.fb-editeur textarea{font-size:16px}.fb-modeles{grid-template-columns:1fr}.fb-editeur-outils{padding:14px}.fb-proprietes-titre{flex-wrap:wrap}}
.fb-proprietes{scroll-margin-top:84px}@media(max-width:720px){.fb-champs-liste ol{display:flex;overflow-x:auto;gap:8px;padding:3px 0 12px;scroll-snap-type:x proximity}.fb-champs-liste ol li{min-width:215px;max-width:215px;scroll-snap-align:start;margin:0}.fb-deplacer{flex-direction:row}.fb-selection{min-width:0}}

`
